import React from 'react';

/**
 * Composant Avatar
 * Le cadre change de couleur et d'effet selon le niveau du joueur :
 * - Niv 1-4   : gris (Débutant)
 * - Niv 5-9   : vert (Intermédiaire)
 * - Niv 10-19 : bleu (Confirmé)
 * - Niv 20-29 : orange (Avancé)
 * - Niv 30-49 : or + glow (Expert)
 * - Niv 50+   : violet + glow animé (Légende)
 */
function Avatar({ user, size = 'md', onClick, className = '', showBorder = false }) {
  const sizeMap = {
    xs:  { cls: 'w-8 h-8 text-xs',   ring: 40 },
    sm:  { cls: 'w-10 h-10 text-sm', ring: 48 },
    md:  { cls: 'w-12 h-12 text-base', ring: 56 },
    lg:  { cls: 'w-16 h-16 text-xl', ring: 72 },
    xl:  { cls: 'w-20 h-20 text-2xl', ring: 88 },
    '2xl': { cls: 'w-24 h-24 text-3xl', ring: 104 },
  };

  // Couleur de cadre selon le niveau
  const getBorderStyle = (level = 1) => {
    if (level >= 50) return {
      border: '3px solid #a855f7',
      boxShadow: '0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4)',
      animation: 'legendPulse 2s ease-in-out infinite',
    };
    if (level >= 30) return {
      border: '3px solid #FFB81C',
      boxShadow: '0 0 10px rgba(255,184,28,0.7), 0 0 20px rgba(255,184,28,0.3)',
    };
    if (level >= 20) return {
      border: '3px solid #f97316',
      boxShadow: '0 0 8px rgba(249,115,22,0.5)',
    };
    if (level >= 10) return {
      border: '3px solid #3b82f6',
      boxShadow: '0 0 8px rgba(59,130,246,0.5)',
    };
    if (level >= 5) return {
      border: '3px solid ##003DA5',
      boxShadow: '0 0 6px rgba(34,197,94,0.4)',
    };
    return {
      border: '2px solid rgba(148,163,184,0.5)',
    };
  };

  // Couleurs de fond selon l'initiale
  const colors = [
    '#7c3aed', '#2563eb', '#4f46e5',
    '#db2777', '#ea580c', '#0d9488',
    '#0891b2', '#e11d48',
  ];
  const getColor = (name) => {
    if (!name) return colors[0];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const level = user?.level || user?.niveau || 1;
  const initiale = user?.pseudo ? user.pseudo[0].toUpperCase() : '?';
  const bgColor = getColor(user?.pseudo);
  const { cls } = sizeMap[size] || sizeMap.md;

  const borderStyle = showBorder ? getBorderStyle(level) : {};
  const cursorStyle = onClick ? { cursor: 'pointer' } : {};

  const commonStyle = {
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'box-shadow 0.3s, border-color 0.3s',
    ...borderStyle,
    ...cursorStyle,
  };

  if (user?.avatar_url || user?.avatar) {
    return (
      <>
        <style>{`
          @keyframes legendPulse {
            0%, 100% { box-shadow: 0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4); }
            50%       { box-shadow: 0 0 20px #a855f7, 0 0 40px rgba(168,85,247,0.7); }
          }
        `}</style>
        <img
          src={user.avatar_url || user.avatar}
          alt={user.pseudo || 'Avatar'}
          onClick={onClick}
          loading="lazy"
          className={`${cls} object-cover ${className}`}
          style={commonStyle}
        />
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes legendPulse {
          0%, 100% { box-shadow: 0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4); }
          50%       { box-shadow: 0 0 20px #a855f7, 0 0 40px rgba(168,85,247,0.7); }
        }
      `}</style>
      <div
        onClick={onClick}
        className={`${cls} flex items-center justify-center font-bold text-white ${className}`}
        style={{
          background: `radial-gradient(circle at 35% 35%, ${bgColor}dd, ${bgColor})`,
          fontFamily: "'Rajdhani', sans-serif",
          ...commonStyle,
        }}
      >
        {initiale}
      </div>
    </>
  );
}

export default Avatar;

